import Box from '@mui/material/Box';
import { alpha, SxProps, Theme, useTheme } from '@mui/material/styles';
import React from 'react';

export interface HighlightedPart {
  highlighted: boolean;
  text: string;
}

interface HighlightOptions {
  normalize?: boolean;
}

interface HighlightedTextProps {
  value?: string | null;
  query?: string;
  normalize?: boolean;
  component?: React.ElementType;
  sx?: SxProps<Theme>;
  markSx?: SxProps<Theme>;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normalizeText(value: string) {
  return value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

function getSearchTokens(query?: string) {
  return Array.from(
    new Set(
      (query || '')
        .split(/[\s,]+/)
        .map((token) => token.trim())
        .filter((token) => token.length >= 2)
    )
  );
}

function getNormalizedIndexMap(value: string) {
  const originalIndexes: number[] = [];
  let normalizedValue = '';

  for (let index = 0; index < value.length; index += 1) {
    if (/[a-zA-Z0-9]/.test(value[index])) {
      normalizedValue += value[index].toLowerCase();
      originalIndexes.push(index);
    }
  }

  return { normalizedValue, originalIndexes };
}

function getLiteralRanges(value: string, token: string) {
  const ranges: Array<{ end: number; start: number }> = [];
  const tokenRegex = new RegExp(escapeRegExp(token.toLowerCase()), 'g');
  let match = tokenRegex.exec(value.toLowerCase());

  while (match) {
    ranges.push({ start: match.index, end: match.index + token.length });
    match = tokenRegex.exec(value.toLowerCase());
  }

  return ranges;
}

function getNormalizedRanges(value: string, token: string) {
  const normalizedToken = normalizeText(token);

  if (normalizedToken.length < 2) {
    return [];
  }

  const { normalizedValue, originalIndexes } = getNormalizedIndexMap(value);
  const tokenRegex = new RegExp(escapeRegExp(normalizedToken), 'g');
  const ranges: Array<{ end: number; start: number }> = [];
  let match = tokenRegex.exec(normalizedValue);

  while (match) {
    const normalizedStart = match.index;
    const normalizedEnd = match.index + normalizedToken.length - 1;

    ranges.push({
      start: originalIndexes[normalizedStart],
      end: originalIndexes[normalizedEnd] + 1,
    });

    match = tokenRegex.exec(normalizedValue);
  }

  return ranges;
}

function mergeRanges(ranges: Array<{ end: number; start: number }>) {
  if (!ranges.length) {
    return [];
  }

  const sortedRanges = [...ranges].sort((left, right) => left.start - right.start);
  const mergedRanges = [sortedRanges[0]];

  for (let index = 1; index < sortedRanges.length; index += 1) {
    const currentRange = sortedRanges[index];
    const previousRange = mergedRanges[mergedRanges.length - 1];

    if (currentRange.start <= previousRange.end) {
      previousRange.end = Math.max(previousRange.end, currentRange.end);
    } else {
      mergedRanges.push(currentRange);
    }
  }

  return mergedRanges;
}

export function getHighlightedParts(value: string, query?: string, { normalize = false }: HighlightOptions = {}) {
  if (!value) {
    return [];
  }

  const tokens = getSearchTokens(query);

  if (!tokens.length) {
    return [{ text: value, highlighted: false }];
  }

  const ranges = mergeRanges(
    tokens.flatMap((token) => [
      ...getLiteralRanges(value, token),
      ...(normalize ? getNormalizedRanges(value, token) : []),
    ])
  );

  if (!ranges.length) {
    return [{ text: value, highlighted: false }];
  }

  const parts: HighlightedPart[] = [];
  let cursor = 0;

  ranges.forEach(({ start, end }) => {
    if (start > cursor) {
      parts.push({ text: value.slice(cursor, start), highlighted: false });
    }

    parts.push({ text: value.slice(start, end), highlighted: true });
    cursor = end;
  });

  if (cursor < value.length) {
    parts.push({ text: value.slice(cursor), highlighted: false });
  }

  return parts;
}

export const HighlightedText: React.FC<React.PropsWithChildren<HighlightedTextProps>> = ({
  value,
  query,
  normalize = false,
  component = 'span',
  sx,
  markSx,
}) => {
  const theme = useTheme();
  const resolvedValue = value || '';
  const defaultMarkSx: SxProps<Theme> = React.useMemo(
    () => ({
      backgroundColor: 'transparent',
      color: 'inherit',
      textDecorationLine: 'underline',
      textDecorationColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.9 : 0.95),
      textDecorationThickness: '0.18em',
      textUnderlineOffset: '0.18em',
      textDecorationSkipInk: 'none',
      fontWeight: 600,
    }),
    [theme]
  );
  const parts = React.useMemo(
    () => getHighlightedParts(resolvedValue, query, { normalize }),
    [normalize, query, resolvedValue]
  );
  const resolvedMarkSx = React.useMemo(
    () => (markSx ? [defaultMarkSx, ...(Array.isArray(markSx) ? markSx : [markSx])] : defaultMarkSx),
    [defaultMarkSx, markSx]
  );

  return (
    <Box component={component} sx={sx}>
      {parts.map((part, index) =>
        part.highlighted ? (
          <Box key={`${resolvedValue}-${index}`} component="mark" sx={resolvedMarkSx}>
            {part.text}
          </Box>
        ) : (
          <React.Fragment key={`${resolvedValue}-${index}`}>{part.text}</React.Fragment>
        )
      )}
    </Box>
  );
};
